// tokenHelpers.ts -----------------------------------------------------------

import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import { notification } from 'antd';
import { API_URL, ENVIRONMENT, DOMAIN } from 'settings';
import { getLogoutMarker, redirectLegacyLogin, rejectBrowserLogin } from 'services/logoutSync';
import { browserLogin, presenceRejected } from 'services/presence';
import { currentLogin } from 'services/loginProtocol';

let refreshPromise: Promise<string> | null = null;
const tokenCookie = `pm_${ENVIRONMENT}_token`;
const refreshCookie = `pm_${ENVIRONMENT}_refresh`;

export async function getValidAccessToken(): Promise<string | undefined> {
  if (redirectLegacyLogin()) throw new Error('Please sign in again.');
  // ❶  If someone else already started a refresh → just wait for it.
  if (refreshPromise) {
    console.log('waiting for the same refresh');
    return refreshPromise;
  }

  const current = Cookies.get(tokenCookie);
  // console.log('current', current);
  if (current) {
    const { exp } = jwtDecode<{ exp: number }>(current);
    const now = Date.now() / 1000;

    // ❷  Token still OK (≥ 30 s left) → use it.
    if (exp - now > 30) {
      console.log('token is still valid');
      return current;
    }
  } else {
    // login request, no token present, carry on with the request without a token
    return undefined;
  }

  // ❸  We *really* need a new access token, start the refresh only once.
  console.log('token expired, starting refresh...');
  const logoutMarker = getLogoutMarker();
  const refresh = Cookies.get(refreshCookie);
  const assertCurrentSession = () => {
    if (
      getLogoutMarker() !== logoutMarker ||
      Cookies.get(refreshCookie) !== refresh
    ) {
      throw new Error('Session changed during token refresh');
    }
  };
  refreshPromise = (async () => {
    const resp = await fetch(`${API_URL}/auth/refresh_token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });

    // A late failure must not clear credentials from a later login.
    assertCurrentSession();
    if (!resp.ok) {
      if (browserLogin(current)) {
        if (resp.status === 401) {
          presenceRejected(current);
          rejectBrowserLogin(current);
        }
        // A Redis/network outage must not clear this browser's credentials.
        throw new Error(resp.status === 401 ? 'Session expired' : 'Login temporarily unavailable');
      }
      // clean up & kick the user out
      console.log('refreshing failed, logging out');
      Cookies.remove(tokenCookie, { domain: DOMAIN });
      Cookies.remove(refreshCookie, { domain: DOMAIN });
      Cookies.remove(`pm_${ENVIRONMENT}_token_exp`, { domain: DOMAIN });
      Cookies.remove(`pm_${ENVIRONMENT}_refresh_exp`, { domain: DOMAIN });
      notification.error({ message: 'Session expired, please login again.' });
      setTimeout(() => (window.location.href = '/auth/login'), 1500);
      throw new Error('Session expired');
    }

    const { access } = await resp.json();
    // Logout can also happen while the response body is being read.
    assertCurrentSession();
    if (!currentLogin(access)) {
      rejectBrowserLogin(current);
      throw new Error('Please sign in again.');
    }
    const { exp } = jwtDecode<{ exp: number }>(access);

    Cookies.set(tokenCookie, access, { sameSite: 'Strict', domain: DOMAIN });
    Cookies.set(`pm_${ENVIRONMENT}_token_exp`, String(exp), {
      sameSite: 'Strict',
      domain: DOMAIN,
    });

    return access;
  })();

  try {
    return await refreshPromise;
  } finally {
    // whether it resolved or threw, allow a new refresh next time
    refreshPromise = null;
  }
}
